import React from 'react';
import PropTypes from 'prop-types';
import Component from '@reactions/component';
import axios from 'axios';
import createReactContext from 'create-react-context';
import { delay } from './utils';
import styles from './styles';

const GDPR_STATE = ['Unknown', 'Yes', 'No'];

function getNewConsentValue(value = '') {
  switch (value) {
    case 'true':
      return GDPR_STATE[1];
    case 'false':
      return GDPR_STATE[2];
    default:
      return GDPR_STATE[0];
  }
}

const FormContext = createReactContext();

function FormConsumer(props) {
  return (
    <FormContext.Consumer>
      {context => {
        if (!context) {
          throw new Error('Form compound component cannot be rendered outside Form component');
        }
        return props.children(context);
      }}
    </FormContext.Consumer>
  );
}

function getConsentValue(checkValue, consentValue) {
  if (typeof consentValue === 'undefined') {
    return false;
  }
  return checkValue.toLowerCase() === 'yes' ? consentValue : !consentValue;
}

FormConsumer.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.element,
    PropTypes.string,
    PropTypes.number,
    PropTypes.func
  ]).isRequired
};

class Form extends React.Component {
  static Group = ({ children }) => (
    <div className="form-group">
      <div className="row">{children}</div>
    </div>
  );

  static Fetch = ({ url, children }) => (
    <FormConsumer>
      {({ handleShowConsent }) => (
        <Component
          initialState={{ loading: true, isGDPR: false }}
          didMount={({ setState }) => {
            axios
              .get(url)
              .then(response => {
                setState({ loading: false, isGDPR: response.data.isGDPR });
                handleShowConsent(response.data.isGDPR);
              })
              .catch(() => setState({ loading: false }));
          }}
        >
          {({ state }) => children(state)}
        </Component>
      )}
    </FormConsumer>
  );

  static ConsentRadioButton = ({ children, id, value }) => (
    <FormConsumer>
      {({ handleConsent, consent }) => (
        <label style={styles.gdpr.radioButtonWrapper} htmlFor={id}>
          <input
            style={styles.gdpr.radioButton}
            id={id}
            type="radio"
            value={value.toLowerCase() === 'yes' ? '1' : ''}
            onChange={handleConsent}
            checked={getConsentValue(value, consent)}
            required
          />
          <span>{children}</span>
        </label>
      )}
    </FormConsumer>
  );

  static Input = ({ type, name, disabled, small, left, right, placeholder, required }) => (
    <FormConsumer>
      {({ setData, data }) => (
        <div
          className={`col-xs-12 ${small ? 'col-sm-6' : ''} ${left ? 'col-left' : ''} ${
            right ? 'col-right' : ''
          }`}
        >
          <input
            className="form-control"
            name={name}
            placeholder={placeholder}
            onChange={event => {
              setData(event.target.name, event.target.value);
            }}
            disabled={disabled}
            value={data[name]}
            type={type}
            required={required}
          />
        </div>
      )}
    </FormConsumer>
  );

  static TextArea = ({ name, disabled, placeholder, required }) => (
    <FormConsumer>
      {({ setData, data }) => (
        <div className="col-xs-12">
          <textarea
            className="form-control textarea"
            name={name}
            placeholder={placeholder}
            disabled={disabled}
            onChange={event => {
              setData(event.target.name, event.target.value);
            }}
            value={data[name]}
            required={required}
          />
        </div>
      )}
    </FormConsumer>
  );

  static SubmitButton = ({ disabled, children }) => (
    <FormConsumer>
      {({ postDataSuccessfully }) => (
        <div className="col-xs-12">
          <button
            className={`btn btn-success btn-lg btn-block ${postDataSuccessfully ? 'success' : ''}`}
            type="submit"
            disabled={disabled}
          >
            {postDataSuccessfully ? 'Thanks for getting in touch!' : children}
          </button>
        </div>
      )}
    </FormConsumer>
  );

  // eslint-disable-next-line react/sort-comp
  setData = (key, value) => {
    this.setState(prevState => ({
      ...prevState,
      data: {
        ...prevState.data,
        [key]: value
      }
    }));
  };

  handleConsent = event => {
    this.setState({ consent: Boolean(event.target.value) });
  };

  handleShowConsent = isGDPR => {
    this.setState({ showConsent: isGDPR });
  };

  // eslint-disable-next-line react/sort-comp
  state = {
    sendingData: false,
    postDataSuccessfully: false,
    setData: this.setData,
    handleConsent: this.handleConsent,
    handleShowConsent: this.handleShowConsent,
    consent: undefined,
    showConsent: false,
    data: {}
  };

  clearData = () => {
    const emptyData = Object.keys(this.state.data).reduce(
      (acc, key) => ({ ...acc, [key]: '' }),
      {}
    );
    this.setState({ data: emptyData });
  };

  submitHandler = event => {
    event.preventDefault();
    this.setState({ sendingData: true });

    const data = {
      ...this.state.data,
      form: this.props.source,
      newConsent: this.state.showConsent
        ? getNewConsentValue(this.state.consent.toString())
        : getNewConsentValue(),
      showConsent: this.state.showConsent
    };

    this.props
      .onSubmit(data)
      .then(() => {
        this.clearData();
        this.setState({ postDataSuccessfully: true, sendingData: false });
        delay(3000).then(() => this.setState({ postDataSuccessfully: false }));
      })
      .catch(() => {
        // eslint-disable-next-line no-undef
        alert('There was a problem submitting your information. Please try again later.');
        this.setState({ sendingData: false });
      });
  };

  render() {
    return (
      <FormContext.Provider value={this.state}>
        <form onSubmit={this.submitHandler}>
          {this.props.children({ submitting: this.state.sendingData })}
        </form>
      </FormContext.Provider>
    );
  }
}

Form.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  source: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.element,
    PropTypes.string,
    PropTypes.number,
    PropTypes.func
  ]).isRequired
};

export default Form;



// WEBPACK FOOTER //
// ./src/pages/partners/components/Form/Form.js